<template>
  <!--
    Presentational record card for the mobile list screens (Units, Accounts,
    Invoices, Inventory, …). The parent supplies the data mapping via props and
    the row actions via the `actions` slot, so each view keeps its existing
    methods — only the presentation changes on phones.
  -->
  <v-card
    flat
    rounded="lg"
    class="mobile-record-card mb-3"
    :ripple="clickable"
    @click="clickable ? $emit('open') : null"
  >
    <div class="mrc-head">
      <v-avatar v-if="icon" size="40" class="mrc-avatar">
        <v-icon :color="iconColor || 'primary'">{{ icon }}</v-icon>
      </v-avatar>
      <div class="mrc-head-text">
        <div class="mrc-title text-truncate">{{ title }}</div>
        <div v-if="subtitle" class="mrc-subtitle text-truncate">
          {{ subtitle }}
        </div>
      </div>
      <div class="mrc-chip">
        <slot name="chip" />
      </div>
    </div>

    <div v-if="fields && fields.length" class="mrc-fields">
      <div v-for="(f, i) in fields" :key="i" class="mrc-field">
        <span class="mrc-label">{{ f.label }}</span>
        <span class="mrc-value" :class="f.class">{{ displayValue(f) }}</span>
      </div>
    </div>

    <div v-if="$slots.actions" class="mrc-actions" @click.stop>
      <slot name="actions" />
    </div>
  </v-card>
</template>

<script>
export default {
  name: "MobileRecordCard",
  props: {
    title: { type: [String, Number], default: "" },
    subtitle: { type: [String, Number], default: "" },
    icon: { type: String, default: "" },
    iconColor: { type: String, default: "" },
    // [{ label, value, class? }]
    fields: { type: Array, default: () => [] },
    clickable: { type: Boolean, default: false },
  },
  methods: {
    displayValue(f) {
      const v = f.value;
      if (v === null || v === undefined || v === "") return "—";
      return v;
    },
  },
};
</script>

<style scoped>
.mobile-record-card {
  border: 1px solid rgba(0, 0, 0, 0.08);
  padding: 12px 14px;
}
.mrc-head {
  display: flex;
  align-items: center;
  gap: 10px;
}
.mrc-avatar {
  background: rgba(25, 118, 210, 0.1);
  flex: 0 0 auto;
}
.mrc-head-text {
  min-width: 0;
  flex: 1 1 auto;
}
.mrc-title {
  font-size: 0.95rem;
  font-weight: 600;
  line-height: 1.2;
}
.mrc-subtitle {
  font-size: 0.78rem;
  opacity: 0.6;
}
.mrc-chip {
  flex: 0 0 auto;
}
.mrc-fields {
  margin-top: 10px;
  display: grid;
  gap: 4px;
}
.mrc-field {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
  font-size: 0.82rem;
}
.mrc-label {
  opacity: 0.55;
  white-space: nowrap;
}
.mrc-value {
  text-align: right;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
}
.mrc-actions {
  margin-top: 10px;
  padding-top: 8px;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 4px;
}

.theme--dark .mobile-record-card {
  border-color: rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.02);
}
.theme--dark .mrc-avatar {
  background: rgba(25, 118, 210, 0.22);
}
.theme--dark .mrc-actions {
  border-top-color: rgba(255, 255, 255, 0.08);
}
</style>
